<template>
  <div>
    <component
      :is="componentType"
      class="menu-item"
      :to="item.to ?? { name: item.name }"
      v-bind="{ ...$attrs, ...hrefAttributes }"
      :class="classes"
      @click="handleClick($event, item)"
    >
      <div class="tw-relative tw-w-full">
        <div v-if="indicator" :id="indicator.key"></div>

        <div
          class="tw-flex tw-items-center"
          :class="{
            'tw-justify-center': collapsed,
          }"
        >
          <div
            class="tw-flex tw-flex-1 tw-shrink-0 tw-items-center tw-overflow-hidden"
            :class="{
              'tw-justify-center': collapsed,
              'tw-gap-4': !collapsed && isStudentView,
              'tw-gap-3': !collapsed && !isStudentView,
            }"
          >
            <div v-if="!isChild" class="menu-item__icon">
              <component
                :is="iconComponent.component"
                v-bind="iconComponent.props"
              />
            </div>

            <div
              v-if="item.meta?.title"
              class="menu-item__title tw-min-w-0 tw-truncate"
              :class="{ 'menu-item__title--collapsed': collapsed }"
            >
              {{ generateMenuTitle(item) }}
            </div>

            <c-menu-badge
              v-if="badge"
              :label="badge?.label"
              :color="badge?.color"
              :text-color="badge?.textColor"
              :class="badge?.class"
              :floating="collapsed"
            />
          </div>

          <div
            v-if="item.children?.length >= 1 && !collapsed"
            class="submenu-button"
            @click.prevent="toggleSubmenu"
          >
            <q-icon
              class="tw-pointer-events-none tw-transition-all"
              size="12px"
              name="fa-solid fa-chevron-right"
              :class="{ '!tw-rotate-90': showSubmenu }"
            />
          </div>
        </div>
      </div>

      <c-tooltip
        v-if="collapsed && !hideTooltip"
        anchor="center right"
        self="center left"
        transition-show="tooltip-slide-in"
        transition-hide="tooltip-slide-out"
      >
        {{ generateMenuTitle(item) }}
      </c-tooltip>
    </component>

    <!-- Desktop Submenu -->
    <transition
      name="submenu"
      @enter="onEnter"
      @after-enter="onAfterEnter"
      @before-leave="onBeforeLeave"
      @leave="onLeave"
    >
      <div
        v-show="
          showSubmenu &&
          item.children?.length >= 1 &&
          !collapsed &&
          !isMobileMenu
        "
        ref="submenu"
        class="menu-item__submenu"
      >
        <menu-link
          v-for="child in item.children"
          :key="child.key ?? child.name"
          class="menu-item--child"
          :item="child"
          is-child
        />
      </div>
    </transition>

    <!-- Mobile submenu -->
    <mobile-submenu
      v-if="item.children?.length && isMobileMenu"
      v-model="showSubmenu"
      :items="item.children"
    />

    <c-menu
      v-if="item.children?.length >= 1 && collapsed"
      v-model="showChildrenInCollapsed"
      v-bind="submenuAttributes"
      class="tw-p-2"
      anchor="center end"
      self="top left"
      :offset="[8, 0]"
    >
      <menu-link
        v-for="child in item.children"
        :key="child.key ?? child.name"
        class="menu-item--child"
        :item="child"
        is-child
      />
    </c-menu>
  </div>
</template>

<script setup>
import MobileSubmenu from "components/layout/MobileSubmenu.vue";
import useHelpers from "composables/useHelpers.js";
import { storeToRefs } from "pinia";
import { useMenuStore } from "store/modules/menuStore.js";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute } from "vue-router";

const props = defineProps({
  item: Object,
  isChild: Boolean,
  iconSize: { type: String, default: "16px" },
  collapsed: Boolean,
  hideTooltip: Boolean,
});

const route = useRoute();
const showChildrenInCollapsed = ref(false);
const isMobileMenu = ref(false);
const submenu = ref(null);
const { generateMenuTitle } = useHelpers();
const menuStore = useMenuStore();
const { menuIsOpen, menuBadges } = storeToRefs(menuStore);
const { isMenuItemExpanded, setMenuItemExpanded } = menuStore;

const badge = computed(
  () => props.item.meta?.badge || menuBadges.value[props.item.name],
);

// Generate a unique key for this menu item's expansion state
const menuItemKey = computed(() => {
  return props.item.key || props.item.name || props.item.meta?.title;
});

// Use computed to sync with store state
const showSubmenu = computed({
  get: () => {
    const storedState = isMenuItemExpanded(menuItemKey.value);

    // If user explicitly set a state, respect it
    if (storedState === false) {
      return false;
    } // Explicitly collapsed by user
    if (storedState === true) {
      return true;
    } // Explicitly expanded by user

    // No stored state yet - auto-expand if child is active and store the state
    if (isChildActive.value && storedState === undefined) {
      // Store the expanded state so it persists across route changes
      setMenuItemExpanded(menuItemKey.value, true);
      return true;
    }

    return false;
  },
  set: (value) => {
    setMenuItemExpanded(menuItemKey.value, value);
  },
});

const componentType = computed(() => {
  if (isMobileMenu.value && props.item.children?.length >= 1) {
    return "div";
  }
  return props.item.href
    ? "a"
    : props.item.name && !isCollapsedAndHaschildren.value
      ? "router-link"
      : "div";
});

const isCollapsedAndHaschildren = computed(() => {
  return props.collapsed && props.item.children?.length >= 1;
});

const isStudentView = computed(() =>
  props.item.meta?.studentView ? true : false,
);

const isActive = computed(() => {
  // Items navigating via an explicit `to` (e.g. param-based routes that share a
  // route name) can't be matched by name alone — siblings would all highlight.
  // Let vue-router's built-in router-link-active class drive their active state.
  if (props.item.to) {
    return false;
  }

  return route.name === props.item.name;
});

const isChildActive = computed(() => {
  // Check if any visible children are active
  const hasActiveChild = props.item.children?.some((child) => {
    // Check by route name
    if (child.name === route.name) {
      return true;
    }

    // Check by exactRouteIdentifier across all matched routes
    if (child.meta?.exactRouteIdentifier) {
      return route.matched.some(
        (matchedRoute) =>
          matchedRoute.meta?.exactRouteIdentifier ===
          child.meta.exactRouteIdentifier,
      );
    }

    return false;
  });

  if (hasActiveChild) {
    return true;
  }

  // For routes with hideSidebarSubmenu, check if current route is a descendant
  // by looking at route.matched (children are stripped from sidebar but route still matches)
  if (props.item.meta?.hideSidebarSubmenu === true) {
    return route.matched.some(
      (matchedRoute) => matchedRoute.name === props.item.name,
    );
  }

  return false;
});

const indicator = computed(() => props.item.meta?.indicator);

const isExactActive = computed(() => {
  const itemSymLink = props.item.meta?.exactRouteIdentifier;

  // Check if any matched route has the same exactRouteIdentifier
  const hasMatchingIdentifier =
    itemSymLink &&
    route.matched.some(
      (matchedRoute) => matchedRoute.meta?.exactRouteIdentifier === itemSymLink,
    );

  // For routes with hideSidebarSubmenu, treat as exact active when a child route is active
  // (children are hidden from sidebar but this parent should show as the active item)
  const isHiddenSubmenuParentActive =
    props.item.meta?.hideSidebarSubmenu === true &&
    route.matched.some((matchedRoute) => matchedRoute.name === props.item.name);

  return (
    (route.name === props.item.name && route.path === props.item.path) ||
    hasMatchingIdentifier ||
    isHiddenSubmenuParentActive
  );
});

const classes = computed(() => ({
  "menu-item--collapsed": props.collapsed,
  "router-link-active": isActive.value || isChildActive.value,
  "router-link-exact-active": isExactActive.value,
  "menu-item--child": props.isChild,
  "menu-item--student-view": isStudentView.value,
  "menu-item--placeholder": props.item.placeholder === true,
  ...(props.item.name ? { [props.item.name]: true } : {}),
}));

const hrefAttributes = computed(() =>
  props.item.href
    ? {
        href: props.item.href,
        target: props.item.target || "_blank",
      }
    : {},
);

const submenuAttributes = computed(() =>
  ["main", "organise"].includes(props.item.meta.menuSection)
    ? {
        anchor: "top right",
        self: "top left",
      }
    : {},
);

const iconComponent = computed(() => {
  const icon = props.item.meta?.icon ?? "fa-solid fa-circle";

  if (icon.startsWith("fa")) {
    return {
      component: QIcon,
      props: {
        name: icon,
        size: props.iconSize,
      },
    };
  }
  return {
    component: "img",
    props: {
      src: icon,
    },
  };
});

const toggleSubmenu = () => {
  // Don't toggle submenu like this on mobile because it's toggled on the parent's handleClick so it triggers double.
  if (isMobileMenu.value) {
    return;
  }
  showSubmenu.value = !showSubmenu.value;
};

const handleClick = (event, item) => {
  const target = event.target;

  const isSubmenuButton = target.classList.contains("submenu-button");
  const hasChildren = item.children?.length >= 1;
  const isPureParent = !item.name && !item.href;

  if (isMobileMenu.value && hasChildren) {
    showSubmenu.value = !showSubmenu.value;
    return;
  }

  if (!isMobileMenu.value && hasChildren && !isSubmenuButton) {
    if (isPureParent) {
      showSubmenu.value = !showSubmenu.value;
    } else if (!showSubmenu.value) {
      showSubmenu.value = true;
    }
  }

  item.action && item.action();
};

const updateMobileState = () => {
  isMobileMenu.value = window.innerWidth < 1024;
  if (!isMobileMenu.value) {
    menuIsOpen.value = false;
  }
};

const onEnter = (el) => {
  el.style.height = el.scrollHeight + "px";
};

const onAfterEnter = (el) => {
  el.style.height = "";
};

const onBeforeLeave = (el) => {
  el.style.height = el.scrollHeight + "px";
};

const onLeave = (el) => {
  void el.offsetHeight;
  el.style.height = "0";
};

onMounted(async () => {
  window.addEventListener("resize", updateMobileState);

  updateMobileState();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateMobileState);
});
</script>

<style lang="scss">
.menu-item {
  @apply tw-relative tw-flex tw-h-[40px] tw-min-w-[40px] tw-cursor-pointer tw-select-none tw-items-center tw-rounded-lg tw-px-3 tw-transition-[background];

  &--collapsed {
    @apply tw-justify-center tw-px-0;
  }

  &:hover {
    @apply tw-bg-gray-100;
  }

  &.font-weight-normal .menu-item__title {
    @apply tw-font-normal;
  }

  &__icon {
    @apply tw-flex tw-h-[20px] tw-w-[20px] tw-shrink-0 tw-items-center tw-justify-center;

    .menu-item--student-view & {
      @apply tw-h-10 tw-w-10;
    }
  }

  &__title {
    @apply tw-whitespace-nowrap tw-font-medium;

    &--collapsed {
      @apply tw-hidden;
    }

    .menu-item--student-view & {
      @apply tw-font-semibold tw-leading-5;
      font-size: 16px;
    }
  }

  &.router-link-active {
    @apply tw-bg-gray-100;
  }

  &.router-link-exact-active {
    @apply tw-bg-green tw-text-white;

    .q-icon {
      @apply tw-font-bold;
    }

    &:not(.menu-item--child) {
      @apply tw-font-semibold;
    }
  }

  &--child {
    @apply tw-text-gray-700;

    .menu-item__title {
      @apply tw-font-normal;
    }
  }

  &--placeholder {
    @apply tw-cursor-default tw-text-gray-400;

    .menu-item__title {
      @apply tw-font-normal tw-italic;
    }

    &:hover {
      @apply tw-bg-transparent;
    }
  }

  &--student-view {
    @apply tw-h-[56px] tw-rounded-2xl tw-py-4 tw-pl-2 tw-pr-4;

    &.menu-item--collapsed {
      @apply tw-size-[56px] tw-justify-center tw-p-2;

      .menu-item__icon {
        @apply tw-h-10 tw-w-10;
      }
    }

    // Override the default active states for student view
    &.router-link-exact-active {
      @apply tw-bg-transparent tw-text-inherit;
    }

    &.router-link-active {
      @apply tw-bg-transparent;
    }

    &.studentMyFeedback {
      &:hover,
      &.router-link-exact-active,
      &.router-link-active {
        @apply tw-bg-aqua-200;
      }
    }

    &.myLearningObjectives {
      &:hover,
      &.router-link-exact-active,
      &.router-link-active {
        @apply tw-bg-red-200;
      }
    }

    &.studentPortfolioIndex {
      &:hover,
      &.router-link-exact-active,
      &.router-link-active {
        @apply tw-bg-purple-200;
      }
    }

    &.studentTasks {
      &:hover,
      &.router-link-exact-active,
      &.router-link-active {
        @apply tw-bg-yellow-200;
      }
    }

    &.studentActionplansIndex {
      &:hover,
      &.router-link-exact-active,
      &.router-link-active {
        @apply tw-bg-blue-200;
      }
    }

    // Each of these selectors is a route name (see the `classes` computed), so renaming a
    // student route silently drops its colour back to the grey default.
    &.studentConversations {
      &:hover,
      &.router-link-exact-active,
      &.router-link-active {
        @apply tw-bg-green-200;
      }
    }
  }

  &__submenu {
    @apply tw-my-1 tw-ml-5 tw-flex tw-flex-col tw-gap-1 tw-overflow-hidden tw-border-l tw-pl-5;
  }

  &__indicator {
    // Inlined `body-extra-small` (styles/modules/_typography.scss): Vite 8
    // processes each style block in isolation, so cross-file custom classes
    // can no longer be @apply'd.
    @apply tw-absolute tw-bottom-full tw-right-1 tw-z-10 -tw-translate-y-1/3 tw-px-1 tw-text-center tw-text-3xs tw-font-normal;

    &--collapsed {
      @apply tw-top-1.5 tw-px-1 tw-text-[8px];
    }
  }

  .submenu-button {
    @apply tw-relative tw-z-10 tw-flex tw-aspect-square tw-w-5 tw-items-center tw-justify-center tw-rounded tw-transition-all hover:tw-bg-gray-300;
  }
}

.submenu-enter-active,
.submenu-leave-active {
  @apply tw-transition-all;
  transition: height 0.3s ease;
}

.submenu-enter-from,
.submenu-leave-to {
  @apply tw-h-0;
}
</style>
